import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useEffect, useState } from 'react';
import { Plus, AlertTriangle, TrendingUp, Package, Edit, Trash2, PackageOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import PageHeader from '../components/shared/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import Table from '../components/ui/Table';
import { currency } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import { useLocalization } from '../hooks/useLocalization';
import { normalizeMoney, onlyDigits } from '../utils/masks';

export default function Estoque() {
  const { settings } = useLocalization();
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showEntradaModal, setShowEntradaModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [items, setItems] = useState([]);
  
  const [formData, setFormData] = useState({
    nome: '',
    codigo: '',
    categoria: '',
    quantidade: 0,
    estoque_minimo: 5,
    preco_custo: 0,
    preco_venda: 0
  });

  const [entradaForm, setEntradaForm] = useState({
    item_id: '',
    quantidade: 1,
    preco_custo: 0
  });

  const categorias = [
    'Óleo', 'Filtros', 'Freios', 'Suspensão', 
    'Elétrica', 'Funilaria', 'Pneus', 'Outros'
  ];

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      
      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id
      
      if (!oficina_id) {
        setLoading(false);
        return;
      }
      
      const { data, error } = await supabase
        .from('estoque')
        .select('*')
        .eq('oficina_id', oficina_id)
        .order('nome');

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error loading items:', error);
      toast.error('Erro ao carregar itens do estoque');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast.error('Nome do item é obrigatório');
      return;
    }

    try {
      setLoading(true);
      
      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id
      
      if (!oficina_id) {
        setLoading(false);
        return;
      }
      
      if (editingItem) {
        // Editar item existente
        const { error } = await supabase
          .from('estoque')
          .update({
            nome: formData.nome.trim(),
            codigo: formData.codigo.trim(),
            categoria: formData.categoria,
            quantidade: parseInt(formData.quantidade),
            estoque_minimo: parseInt(formData.estoque_minimo),
            preco_custo: parseFloat(formData.preco_custo),
            preco_venda: parseFloat(formData.preco_venda)
          })
          .eq('id', editingItem.id)
          .eq('oficina_id', oficina_id);

        if (error) throw error;
        toast.success('Item atualizado com sucesso!');
      } else {
        // Criar novo item
        const { error } = await supabase
          .from('estoque')
          .insert({
            nome: formData.nome.trim(),
            codigo: formData.codigo.trim(),
            categoria: formData.categoria,
            quantidade: parseInt(formData.quantidade),
            estoque_minimo: parseInt(formData.estoque_minimo),
            preco_custo: parseFloat(formData.preco_custo),
            preco_venda: parseFloat(formData.preco_venda),
            oficina_id: oficina_id
          });

        if (error) throw error;
        toast.success('Item adicionado com sucesso!');
      }

      // Resetar formulário
      setFormData({
        nome: '',
        codigo: '',
        categoria: '',
        quantidade: 0,
        estoque_minimo: 5,
        preco_custo: 0,
        preco_venda: 0
      });
      setEditingItem(null);
      setShowModal(false);
      loadItems();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error('Erro ao salvar item');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nome: item.nome,
      codigo: item.codigo || '',
      categoria: item.categoria || '',
      quantidade: item.quantidade,
      estoque_minimo: item.estoque_minimo,
      preco_custo: item.preco_custo,
      preco_venda: item.preco_venda
    });
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      
      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id
      
      if (!oficina_id) {
        setLoading(false);
        return;
      }
      
      const { error } = await supabase
        .from('estoque')
        .delete()
        .eq('id', itemToDelete.id)
        .eq('oficina_id', oficina_id);

      if (error) throw error;

      toast.success('Item excluído com sucesso!');
      setShowDeleteModal(false);
      setItemToDelete(null);
      loadItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Erro ao excluir item');
    } finally {
      setLoading(false);
    }
  };

  const handleEntrada = async (e) => {
    e.preventDefault();
    
    if (!entradaForm.item_id) {
      toast.error('Selecione um item');
      return;
    }

    try {
      setLoading(true);
      
      const { data: authData } = await supabase.auth.getUser()
      const { data: userRow } = await supabase
        .from('usuarios')
        .select('oficina_id')
        .eq('email', authData.user.email)
        .single()
      const oficina_id = userRow?.oficina_id
      
      if (!oficina_id) {
        setLoading(false);
        return;
      }
      
      // Buscar item atual
      const { data: currentItem, error: fetchError } = await supabase
        .from('estoque')
        .select('*')
        .eq('id', entradaForm.item_id)
        .single();

      if (fetchError) throw fetchError;

      // Atualizar quantidade
      const novaQuantidade = currentItem.quantidade + parseInt(entradaForm.quantidade);
      const { error: updateError } = await supabase
        .from('estoque')
        .update({
          quantidade: novaQuantidade,
          preco_custo: parseFloat(entradaForm.preco_custo) || currentItem.preco_custo
        })
        .eq('id', entradaForm.item_id)
        .eq('oficina_id', oficina_id);

      if (updateError) throw updateError;

      await supabase
        .from('estoque_movimentacoes')
        .insert({
          oficina_id,
          estoque_id: entradaForm.item_id,
          tipo: 'entrada',
          quantidade: parseInt(entradaForm.quantidade),
          custo_unitario: parseFloat(entradaForm.preco_custo) || currentItem.preco_custo,
          observacao: 'Entrada manual de estoque',
        });

      toast.success(`Entrada de ${entradaForm.quantidade}x ${currentItem.nome} registrada!`);
      
      // Resetar formulário
      setEntradaForm({
        item_id: '',
        quantidade: 1,
        preco_custo: 0
      });
      setShowEntradaModal(false);
      loadItems();
    } catch (error) {
      console.error('Error registering entrada:', error);
      toast.error('Erro ao registrar entrada');
    } finally {
      setLoading(false);
    }
  };

  // Calcular estatísticas
  const valorTotalEstoque = (items || []).reduce((sum, item) => sum + (item.quantidade * item.preco_custo), 0);
  const itensCriticos = (items || []).filter(item => item.quantidade <= item.estoque_minimo);
  const valorTotalVenda = (items || []).reduce((sum, item) => sum + (item.quantidade * item.preco_venda), 0);

  // Dados para o gráfico
  const chartData = categorias.map(categoria => ({
    categoria,
    valor: (items || [])
      .filter(item => item.categoria === categoria)
      .reduce((sum, item) => sum + (item.quantidade * item.preco_custo), 0)
  }));

  const columns = [
    { key: 'nome', label: 'Nome' },
    { key: 'codigo', label: 'Código' },
    { key: 'categoria', label: 'Categoria' },
    { 
      key: 'quantidade', 
      label: 'Quantidade',
      render: (item) => (
        <span className={item.quantidade <= item.estoque_minimo ? 'text-red-400 font-semibold' : ''}>
          {item.quantidade}
          {item.quantidade <= item.estoque_minimo && (
            <AlertTriangle size={14} className="inline ml-2 text-red-400 animate-pulse" />
          )}
        </span>
      )
    },
    { 
      key: 'estoque_minimo', 
      label: 'Estoque Mínimo',
      render: (item) => item.estoque_minimo
    },
    { 
      key: 'preco_custo', 
      label: 'Preço de Custo',
      render: (item) => currency(item.preco_custo)
    },
    { 
      key: 'preco_venda', 
      label: 'Preço de Venda',
      render: (item) => currency(item.preco_venda)
    },
    {
      key: 'actions',
      label: 'Ações',
      render: (item) => (
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleEdit(item)}
            disabled={loading}
          >
            <Edit size={14} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => handleDelete(item)}
            disabled={loading}
            className="text-red-400 hover:text-red-300"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Carregando estoque...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader 
        title="Estoque" 
        subtitle="Controle completo de peças e materiais" 
      />

      {/* Cards de métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent/10 p-3">
              <Package className="text-accent" size={20} />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Total de Itens</p>
              <p className="text-2xl font-bold">{items.length}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-danger/10 p-3">
              <AlertTriangle className="text-danger" size={20} />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Itens Críticos</p>
              <p className="text-2xl font-bold text-danger">{itensCriticos.length}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-success/10 p-3">
              <TrendingUp className="text-success" size={20} />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Valor Total</p>
              <p className="text-2xl font-bold">{currency(valorTotalEstoque)}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-sky-500/10 p-3">
              <Bar className="text-sky-500" size={20} />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Valor de Venda</p>
              <p className="text-2xl font-bold">{currency(valorTotalVenda)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Gráfico */}
      <Card>
        <h3 className="text-lg font-semibold mb-4">Valor por Categoria</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis 
              dataKey="categoria" 
              stroke="#9ca3af" 
              tick={{ fill: "#9ca3af" }}
            />
            <YAxis 
              stroke="#9ca3af" 
              tick={{ fill: "#9ca3af" }}
            />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: "#1f2937", 
                border: "1px solid #374151",
                borderRadius: "8px"
              }}
              formatter={(value) => currency(value)}
            />
            <Bar 
              dataKey="valor" 
              fill="#f97316"
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Tabela */}
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Itens em Estoque</h3>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowEntradaModal(true)}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              <PackageOpen size={16} className="mr-2" />
              Registrar Entrada
            </Button>
            <Button
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              <Plus size={16} className="mr-2" />
              Adicionar Item
            </Button>
          </div>
        </div>

        <Table
          columns={columns}
          rows={items || []}
          loading={loading}
          emptyMessage="Nenhum item encontrado no estoque"
        />
      </Card>

      {/* Modal Adicionar/Editar Item */}
      <Modal 
        open={showModal} 
        onClose={() => {
          setShowModal(false);
          setEditingItem(null);
          setFormData({
            nome: '',
            codigo: '',
            categoria: '',
            quantidade: 0,
            estoque_minimo: 5,
            preco_custo: 0,
            preco_venda: 0
          });
        }}
        title={editingItem ? 'Editar Item' : 'Adicionar Item'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Nome do item *</label>
            <Input
              placeholder="Nome do item"
              value={formData.nome}
              onChange={(e) => setFormData(prev => ({ ...prev, nome: e.target.value }))}
              disabled={loading}
              required
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Código SKU</label>
            <Input
              placeholder="Código do item"
              value={formData.codigo}
              onChange={(e) => setFormData(prev => ({ ...prev, codigo: e.target.value }))}
              disabled={loading}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Categoria</label>
            <select
              value={formData.categoria}
              onChange={(e) => setFormData(prev => ({ ...prev, categoria: e.target.value }))}
              disabled={loading}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
            >
              <option value="">Selecione...</option>
              {(categorias || []).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Quantidade</label>
              <Input
                placeholder="0"
                mask="integer"
                value={formData.quantidade}
                onChange={(e) => setFormData(prev => ({ ...prev, quantidade: onlyDigits(e.target.value, 6) }))}
                disabled={loading}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Estoque Mínimo</label>
              <Input
                placeholder="5"
                mask="integer"
                value={formData.estoque_minimo}
                onChange={(e) => setFormData(prev => ({ ...prev, estoque_minimo: onlyDigits(e.target.value, 6) }))}
                disabled={loading}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Preco de custo ({settings.currency})</label>
              <Input
                placeholder="Ex: 0,00"
                mask="money"
                value={formData.preco_custo}
                onChange={(e) => setFormData(prev => ({ ...prev, preco_custo: normalizeMoney(e.target.value) }))}
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Preco de venda ({settings.currency})</label>
            <Input
              placeholder="Ex: 0,00"
              mask="money"
              value={formData.preco_venda}
              onChange={(e) => setFormData(prev => ({ ...prev, preco_venda: normalizeMoney(e.target.value) }))}
              disabled={loading}
            />
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowModal(false);
                setEditingItem(null);
                setFormData({
                  nome: '',
                  codigo: '',
                  categoria: '',
                  quantidade: 0,
                  estoque_minimo: 5,
                  preco_custo: 0,
                  preco_venda: 0
                });
              }}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Salvando...' : (editingItem ? 'Atualizar' : 'Adicionar')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Entrada de Estoque */}
      <Modal 
        open={showEntradaModal} 
        onClose={() => {
          setShowEntradaModal(false);
          setEntradaForm({
            item_id: '',
            quantidade: 1,
            preco_custo: 0
          });
        }}
        title="Registrar Entrada no Estoque"
      >
        <form onSubmit={handleEntrada} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Item *</label>
            <select
              value={entradaForm.item_id}
              onChange={(e) => setEntradaForm(prev => ({ ...prev, item_id: e.target.value }))}
              disabled={loading}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-white focus:border-accent focus:outline-none"
              required
            >
              <option value="">Selecione um item...</option>
              {(items || []).map(item => (
                <option key={item.id} value={item.id}>
                  {item.nome} (Estoque atual: {item.quantidade})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Quantidade</label>
              <Input
                placeholder="1"
                mask="integer"
                value={entradaForm.quantidade}
                onChange={(e) => setEntradaForm(prev => ({ ...prev, quantidade: onlyDigits(e.target.value, 6) }))}
                disabled={loading}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Preco de custo ({settings.currency})</label>
              <Input
                placeholder="Ex: 0,00"
                mask="money"
                value={entradaForm.preco_custo}
                onChange={(e) => setEntradaForm(prev => ({ ...prev, preco_custo: normalizeMoney(e.target.value) }))}
                disabled={loading}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setShowEntradaModal(false);
                setEntradaForm({
                  item_id: '',
                  quantidade: 1,
                  preco_custo: 0
                });
              }}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Registrando...' : 'Registrar Entrada'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal de Confirmação de Exclusão */}
      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirmar Exclusão">
        <div className="space-y-4">
          <p className="text-zinc-300">
            Tem certeza que deseja excluir o item "<span className="font-semibold">{itemToDelete?.nome}</span>"?
          </p>
          <p className="text-sm text-zinc-400">
            Esta ação não pode ser desfeita.
          </p>
          
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Cancelar
            </Button>
            <Button onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Confirmar Exclusão
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
